import React, { useRef, useEffect, useState, useMemo } from 'react';

import { useFreecodeContextRun, useFreecodeContextData, useFreecodeContextLayout } from 'hooks/useContext';
import { categories } from 'constants/tests';
import { editorTypes } from 'components/FreecodePage/FreecodeLayout/FreecodeLayoutConstants';
import ResizableColumns from 'components/ResizableColumns/ResizableColumns';
import {
  ConsoleScrollContainer,
  ConsoleContainer,
  ConsoleLogStep,
  ConsoleText,
  ConsoleLogStepNumber,
  BoldHover,
} from './styles';
import ExerciseProgress from './ExerciseProgress/ExerciseProgress';

export default function Console({ runFunction, nextButtonSettings }) {
  const { test, testImpl } = useFreecodeContextData();
  const { editorType } = useFreecodeContextLayout();
  const { isRunning, runResponse, levelToRun, canvasStep, consoleLogSteps } = useFreecodeContextRun();

  const [scrollToBottom, setScrollToBottom] = useState(true);
  const [consoleContent, setConsoleContent] = useState(0);

  const consoleScrollRef = useRef(null);

  const runCode = () => runFunction && runFunction();

  const currStepCount = useMemo(() => {
    if (!consoleLogSteps?.length) return 0;
    if (consoleLogSteps.length <= 2) return consoleLogSteps.length;
    if (!testImpl?.configCanvas?.canvasAnimated) return consoleLogSteps?.length - 1;
    return canvasStep + 1;
  }, [testImpl?.configCanvas, consoleLogSteps, canvasStep]);

  useEffect(() => {
    if (!isRunning) return;
    setConsoleContent(null);
  }, [isRunning]);

  useEffect(() => {
    if (scrollToBottom && consoleScrollRef.current?.lastChild) {
      consoleScrollRef.current.lastChild.scrollIntoView?.({ behavior: 'instant', block: 'end' });
    }
  }, [currStepCount, scrollToBottom]);

  useEffect(() => {
    if (!consoleLogSteps?.length) return;

    let maxLogSteps = consoleLogSteps.length || 2;
    maxLogSteps = maxLogSteps <= 2 ? maxLogSteps - 1 : maxLogSteps - 2;

    setConsoleContent(
      consoleLogSteps.map((stepData, stepIndex) => (
        <ConsoleLogStep key={stepIndex}>
          <span>
            {stepData?.log?.map((logEntry, logEntryIndex) => (
              <ConsoleText key={logEntryIndex} pipe={logEntry.pipe} tag={logEntry.tag}>
                {logEntry?.message || ''}
              </ConsoleText>
            ))}
          </span>
          <ConsoleLogStepNumber>
            {stepIndex}/{maxLogSteps}
          </ConsoleLogStepNumber>
        </ConsoleLogStep>
      ))
    );
  }, [consoleLogSteps, levelToRun]);

  const consoleOnScroll = () => {
    setScrollToBottom(
      consoleScrollRef.current.scrollHeight < consoleScrollRef.current.clientHeight ||
        consoleScrollRef.current.scrollHeight - consoleScrollRef.current.scrollTop <
          consoleScrollRef.current.clientHeight + 5
    );
  };

  // Initial display before the first run code
  const initialDisplay = (
    <ConsoleContainer>
      <ConsoleScrollContainer>
        <ConsoleText>
          {`${editorType === editorTypes.liveCoding ? 'Candidate' : 'Your'} program output appears here.`}
          {editorType !== editorTypes.liveCoding && (
            <>
              <br />
              Click on{' '}
              <i onClick={runCode}>
                <BoldHover>Run Code</BoldHover>
              </i>{' '}
              to execute
            </>
          )}
        </ConsoleText>
      </ConsoleScrollContainer>
    </ConsoleContainer>
  );

  const isRunningDisplay = (
    <ConsoleContainer>
      <ConsoleScrollContainer>
        <ConsoleText>Processing your code...</ConsoleText>
      </ConsoleScrollContainer>
    </ConsoleContainer>
  );

  const errorDisplay = (
    <ConsoleContainer>
      <ConsoleScrollContainer>
        <ConsoleText pipe={'err'}>{runResponse?.compileErrors?.[0]}</ConsoleText>
      </ConsoleScrollContainer>
    </ConsoleContainer>
  );

  const runLogs = (
    <ConsoleContainer>
      <ConsoleScrollContainer ref={consoleScrollRef} onScroll={consoleOnScroll}>
        {consoleContent && consoleContent.slice(0, currStepCount)}
        {/* Do special logic for last step in order to display errors */}
        {consoleContent?.length >= 3 &&
          currStepCount === consoleLogSteps.length - 2 &&
          consoleLogSteps[currStepCount]?.log?.length > 0 &&
          consoleContent.slice(currStepCount, currStepCount + 1)}
      </ConsoleScrollContainer>
    </ConsoleContainer>
  );

  const consoleDisplay = () => {
    if (isRunning) return isRunningDisplay;
    if (
      test.category === categories.EXERCISE.category &&
      levelToRun !== runResponse?.levelToRun &&
      consoleLogSteps?.length === 0
    )
      return initialDisplay;
    if (runResponse?.compileErrors?.[0]) return errorDisplay;
    if (runResponse || consoleLogSteps?.length > 0) return runLogs;
    return initialDisplay;
  };

  const exerciseDisplay = () => (
    <ResizableColumns sizes={[75, 25]}>
      {consoleDisplay()}
      <ExerciseProgress nextButtonSettings={nextButtonSettings} />
    </ResizableColumns>
  );

  return categories.EXERCISE.category === test.category ? exerciseDisplay() : consoleDisplay();
}
